import React, { ReactElement, useMemo } from 'react';
import SubPage from '#/common/components/SubPage';
import useSingleStatsQuery from '../hooks/useSingleStatsQuery';
import DashboardItem from '../components/DashboardItem';
import useDashboardItems, { ItemKeys } from '../hooks/useDashboardItems';
import { useNavigate } from 'react-router-dom';
import { DashboardStatsPoint } from '#/graphql/types';
import PieChart from '../components/PieChart';
import Dashboard from '../components/Dashboard';
import BarChart from '../components/BarChart';
import DashboardCards from '../components/DashboardCards';
import DashboardConfig from '../components/DashboardConfig';
import { useSwitch } from '@nuvolo/nuux-core/hooks/useSwitch';
import { NOT_ASSIGNED_LABEL, NULL_FILTER } from '../utils/constants';
import { categories, priorities } from '#/modules/workOrder/mappings/mappings';
import { orderBy } from 'lodash';
import { NuvoPieChartProps } from '@nuvolo/nuux-chart/components/NuvoPieChart';
import useSession from '#/common/hooks/useSession';
import { DASHBOARD_COLORS } from '../constants/chartColors';
import StackedBarChart from '../components/StackedBarChart';
import { NuvoTooltip } from '@nuvolo/nuux-core/components/NuvoTooltip';
import PageActions from '#/common/components/SubPage/PageActions/PageActions';
import { PageAction } from '#/app/providers/PageProvider';
import FAIcon from '#/common/components/FAIcon';

const conditionOrder = ['green', 'amber', 'red', null];

type DashboardStatsPointValues = DashboardStatsPoint & {
  label: string;
};

const sort = (
  stats: (DashboardStatsPoint | undefined | null)[] | undefined | null,
  order: (string | null)[],
) => {
  const result = stats
    ?.filter((s): s is DashboardStatsPointValues => !!s)
    .sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));

  return result;
};

const DashboardPage: React.FC = () => {
  const session = useSession();
  const navigate = useNavigate();
  const { items, saveItems, defaultItems, defaultItemsJson } = useDashboardItems();

  const loading = !session.user?.id;
  const visibleItems = useMemo(() => items.filter((item) => item.show), [items]);

  // Create individual queries for each visible chart using the new hook
  const queries = {
    assetsPerCondition: useSingleStatsQuery('assets-per-condition', visibleItems),
    assetsPerLocation: useSingleStatsQuery('assets-per-location', visibleItems),
    assetsPerType: useSingleStatsQuery('assets-per-type', visibleItems),
    assetsPerExpenses: useSingleStatsQuery('assets-per-expenses', visibleItems),
    workOrdersPerCategory: useSingleStatsQuery('work-orders-per-category', visibleItems),
    workOrdersPerPriority: useSingleStatsQuery('work-orders-per-priority', visibleItems),
    workOrdersPerState: useSingleStatsQuery('work-orders-per-state', visibleItems),
    workOrdersPerDueDate: useSingleStatsQuery('work-orders-per-due-date', visibleItems),
    myWorkOrdersPerCategory: useSingleStatsQuery('my-work-orders-per-category', visibleItems),
    myWorkOrdersPerPriority: useSingleStatsQuery('my-work-orders-per-priority', visibleItems),
    myWorkOrdersPerState: useSingleStatsQuery('my-work-orders-per-state', visibleItems),
    myWorkOrdersPerDueDate: useSingleStatsQuery('my-work-orders-per-due-date', visibleItems),
    findingsPerType: useSingleStatsQuery('findings-per-type', visibleItems),
    findingsPerPriority: useSingleStatsQuery('findings-per-priority', visibleItems),
    findingsPerAssetModel: useSingleStatsQuery('findings-per-asset-model', visibleItems),
    workOrdersSLAData: useSingleStatsQuery('work-orders-sla-data', visibleItems),
  };

  /**
   * Navigate to a grid with filters in JSON format
   * DevExtreme filter format: [["field", "operator", "value"]]
   * URL example: /module?filters=[["status","=","active"]]
   */
  const navigateToGrid = (
    module: string,
    field: string,
    value: string | null | undefined,
    keepOriginalValue: boolean = false,
  ) => {
    const filterValue =
      (value === NOT_ASSIGNED_LABEL || value === null) && !keepOriginalValue ? NULL_FILTER : value;

    const filters = [[field, '=', filterValue]];
    const params = new URLSearchParams();
    params.set('filters', JSON.stringify(filters));

    navigate(`/${module}?${params.toString()}`);
  };

  const navigateToGridByHeaderFilter = (
    module: string,
    field: string,
    value: string | null | undefined,
  ) => {
    if (!value) return;
    // anyof format matches the header-filter chip style used by ID-based columns
    const filters = [field, 'anyof', [value]];
    const params = new URLSearchParams();
    params.set('filters', JSON.stringify(filters));
    navigate(`/${module}?${params.toString()}`);
  };

  const navigateToWoPerDueDate = (value: string | null | undefined) => {
    const date = new Date().toISOString().split('T')[0].replace(/-/g, '/');
    let operator = '=';
    let filterValue = date;

    if (value === 'past') {
      operator = '<'; // Less than - overdue work orders
      filterValue = date;
    } else if (value === 'future') {
      operator = '>'; // Greater than - upcoming work orders
      filterValue = date;
    }

    const filters = [['dueDate', operator, filterValue]];
    const params = new URLSearchParams();
    params.set('filters', JSON.stringify(filters));

    if (value === 'future') {
      params.set('sortBy', 'dueDate');
    }

    navigate(`/work-orders/list?${params.toString()}`);
  };

  const navigateToMyWorkOrders = (fieldValuePairs: {
    [key in string]: string | null | undefined;
  }) => {
    // DevExtreme filter with multiple conditions: [condition1, "and", condition2, "and", ...]
    // Note: '<>' is DevExtreme's "not equal" operator (SQL-style, not '!=')
    const filters: any[] = [
      ['assignedToId', '=', session?.user?.id],
      'and',
      ['state', '<>', 'closed'], // '<>' means "not equal"
    ];

    Object.entries(fieldValuePairs).forEach(([field, value]) => {
      const filterValue = value === NOT_ASSIGNED_LABEL || value === null ? NULL_FILTER : value;
      filters.push('and', [field, '=', filterValue]);
    });

    const params = new URLSearchParams();
    params.set('filters', JSON.stringify(filters));
    navigate(`/work-orders/list?${params.toString()}`);
  };
  const itemRenderers: Record<
    ItemKeys,
    {
      data: any;
      loading: boolean;
      element: ReactElement;
      configObj?: { customButtons?: ReactElement };
    }
  > = useMemo(() => {
    return {
      'assets-per-condition': {
        data: sort(queries.assetsPerCondition.data?.assetsPerCondition, conditionOrder),
        loading: queries.assetsPerCondition.loading,
        element: (
          <DashboardCards
            dataSource={sort(queries.assetsPerCondition.data?.assetsPerCondition, conditionOrder)}
            colors={DASHBOARD_COLORS.assetCondition}
            onItemClick={(cond: DashboardStatsPoint) =>
              navigateToGrid('assets/list', 'condition', cond.filterByValue)
            }
          />
        ),
      },
      'assets-per-location': {
        data: queries.assetsPerLocation.data?.assetsPerLocation,
        loading: queries.assetsPerLocation.loading,
        element: (
          <PieChart
            type="donut"
            dataSource={queries.assetsPerLocation.data?.assetsPerLocation as DashboardStatsPoint[]}
            center={{
              title: queries.assetsPerLocation.data?.totalAssets?.toLocaleString(),
              subtitle: 'Total Assets',
            }}
            onItemClick={(loc: DashboardStatsPoint) =>
              navigateToGridByHeaderFilter('assets/list', 'location.id', loc.filterByValue)
            }
          />
        ),
      },
      'assets-per-type': {
        data: queries.assetsPerType.data?.assetsPerType,
        loading: queries.assetsPerType.loading,
        element: (
          <PieChart
            startingDataColor="gray"
            dataSource={queries.assetsPerType.data?.assetsPerType as DashboardStatsPoint[]}
            onItemClick={(type: DashboardStatsPoint) =>
              navigateToGrid('assets/list', 'assetType.name', type.label, true)
            }
          />
        ),
      },
      'assets-per-expenses': {
        data: queries.assetsPerExpenses.data?.assetsPerExpenses,
        loading: queries.assetsPerExpenses.loading,
        element: (
          <BarChart
            dataSource={queries.assetsPerExpenses.data?.assetsPerExpenses}
            noDataText="There are no expenses attached to assets!"
            startingDataColor="gold"
            onItemClick={(item) => {
              navigateToGrid('assets/list', 'model.name', item.label, true);
            }}
            setup={{
              valueAxis: 'Cost',
              argumentAxis: 'Asset Model',
            }}
          />
        ),
      },
      'work-orders-per-category': {
        data: queries.workOrdersPerCategory.data?.workOrdersPerCategory,
        loading: queries.workOrdersPerCategory.loading,
        element: (
          <PieChart
            startingDataColor="orange"
            dataSource={
              queries.workOrdersPerCategory.data?.workOrdersPerCategory as DashboardStatsPoint[]
            }
            legendMappings={categories}
            onItemClick={(cat: DashboardStatsPoint) =>
              navigateToGrid('work-orders/list', 'category', cat.filterByValue)
            }
          />
        ),
      },
      'work-orders-per-priority': {
        data: queries.workOrdersPerPriority.data?.workOrdersPerPriority,
        loading: queries.workOrdersPerPriority.loading,
        element: (
          <PieChart
            type="donut"
            dataSource={
              orderBy(
                queries.workOrdersPerPriority.data?.workOrdersPerPriority,
                (item) => item?.filterByValue ?? '',
                'desc',
              ) as DashboardStatsPoint[]
            }
            startingDataColor={'priorityCritical' as NuvoPieChartProps['startingDataColor']}
            center={{
              title: queries.workOrdersPerPriority.data?.totalWorkOrders?.toLocaleString(),
              subtitle: 'Total Work Orders',
            }}
            legendMappings={priorities}
            onItemClick={(priority: DashboardStatsPoint) =>
              navigateToGrid('work-orders/list', 'priority', priority.filterByValue)
            }
          />
        ),
      },
      'my-work-orders-per-state': {
        data: queries.myWorkOrdersPerState.data?.workOrdersPerState,
        loading: queries.myWorkOrdersPerState.loading,
        element: (
          <DashboardCards
            dataSource={
              queries.myWorkOrdersPerState.data?.workOrdersPerState as DashboardStatsPoint[]
            }
            colors={DASHBOARD_COLORS.workOrderState}
            onItemClick={(state: DashboardStatsPoint) =>
              state.filterByValue === 'newly_assigned_to_me'
                ? navigateToMyWorkOrders({ seenByAssignee: 'false', isArchived: 'false' })
                : navigateToMyWorkOrders({ state: state.filterByValue })
            }
          />
        ),
      },
      'my-work-orders-per-priority': {
        data: queries.myWorkOrdersPerPriority.data?.workOrdersPerPriority,
        loading: queries.myWorkOrdersPerPriority.loading,
        element: (
          <PieChart
            type="donut"
            dataSource={
              orderBy(
                queries.myWorkOrdersPerPriority.data?.workOrdersPerPriority,
                (item) => item?.filterByValue ?? '',
                'desc',
              ) as DashboardStatsPoint[]
            }
            startingDataColor={'priorityCritical' as NuvoPieChartProps['startingDataColor']}
            center={{
              title: queries.myWorkOrdersPerPriority.data?.totalWorkOrders?.toLocaleString(),
              subtitle: 'My Work Orders',
            }}
            legendMappings={priorities}
            onItemClick={(priority: DashboardStatsPoint) =>
              navigateToMyWorkOrders({ priority: priority.filterByValue })
            }
          />
        ),
      },
      'my-work-orders-per-category': {
        data: queries.myWorkOrdersPerCategory.data?.workOrdersPerCategory,
        loading: queries.myWorkOrdersPerCategory.loading,
        element: (
          <PieChart
            startingDataColor="orange"
            dataSource={
              queries.myWorkOrdersPerCategory.data?.workOrdersPerCategory as DashboardStatsPoint[]
            }
            legendMappings={categories}
            onItemClick={(cat: DashboardStatsPoint) =>
              navigateToMyWorkOrders({ category: cat.filterByValue })
            }
          />
        ),
      },
      'my-work-orders-per-due-date': {
        data: queries.myWorkOrdersPerDueDate.data?.workOrdersPerDueDate,
        loading: queries.myWorkOrdersPerDueDate.loading,
        element: (
          <BarChart
            dataSource={queries.myWorkOrdersPerDueDate.data?.workOrdersPerDueDate}
            noDataText="No work orders assigned to me in the next 7 days."
            startingDataColor="darkGray"
            yTickInterval={1}
            onItemClick={(item) => {
              navigateToMyWorkOrders({ dueDate: item.filterByValue });
            }}
            setup={{
              valueAxis: 'Work Order Count',
              argumentAxis: 'Date',
            }}
          />
        ),
      },
      'work-orders-per-state': {
        data: queries.workOrdersPerState.data?.workOrdersPerState,
        loading: queries.workOrdersPerState.loading,
        element: (
          <DashboardCards
            dataSource={
              queries.workOrdersPerState.data?.workOrdersPerState as DashboardStatsPoint[]
            }
            colors={DASHBOARD_COLORS.workOrderState}
            onItemClick={(state: DashboardStatsPoint) =>
              navigateToGrid('work-orders/list', 'state', state.filterByValue)
            }
          />
        ),
      },
      'work-orders-per-due-date': {
        data: queries.workOrdersPerDueDate.data?.workOrdersPerDueDate,
        loading: queries.workOrdersPerDueDate.loading,
        element: (
          <BarChart
            dataSource={queries.workOrdersPerDueDate.data?.workOrdersPerDueDate}
            noDataText="No work orders due next 7 days."
            startingDataColor="darkGray"
            yTickInterval={1}
            onItemClick={(item) => {
              navigateToGrid('work-orders/list', 'dueDate', item.filterByValue);
            }}
            setup={{
              valueAxis: 'Work Order Count',
              argumentAxis: 'Date',
            }}
          />
        ),
      },
      'findings-per-type': {
        data: queries.findingsPerType.data?.findingsPerType,
        loading: queries.findingsPerType.loading,
        element: (
          <DashboardCards
            dataSource={queries.findingsPerType.data?.findingsPerType as DashboardStatsPoint[]}
            colors={DASHBOARD_COLORS.findingType}
            onItemClick={(type: DashboardStatsPoint) =>
              navigateToGrid('findings', 'type', type.filterByValue)
            }
          />
        ),
      },
      'findings-per-priority': {
        data: queries.findingsPerPriority.data?.findingsPerPriority,
        loading: queries.findingsPerPriority.loading,
        element: (
          <PieChart
            type="donut"
            dataSource={
              queries.findingsPerPriority.data?.findingsPerPriority as DashboardStatsPoint[]
            }
            startingDataColor={'findPriorityPlaning' as NuvoPieChartProps['startingDataColor']}
            center={{
              title: queries.findingsPerPriority.data?.totalFindings?.toLocaleString(),
              subtitle: 'Total Findings',
            }}
            onItemClick={(priority: DashboardStatsPoint) =>
              navigateToGrid('findings', 'priority', priority.filterByValue)
            }
          />
        ),
      },
      'findings-per-asset-model': {
        data: queries.findingsPerAssetModel.data?.findingsPerAssetModel,
        loading: queries.findingsPerAssetModel.loading,
        element: (
          <BarChart
            dataSource={
              queries.findingsPerAssetModel.data?.findingsPerAssetModel as DashboardStatsPoint[]
            }
            noDataText="No findings reported."
            startingDataColor="red"
            onItemClick={(model) => navigateToGrid('findings', 'model.name', model.label, true)}
            yTickInterval={1}
            setup={{
              valueAxis: 'Number of Findings',
              argumentAxis: 'Asset Model',
            }}
          />
        ),
      },
      'work-orders-sla-data': {
        data: queries.workOrdersSLAData.data?.workOrdersSLAData,
        loading: queries.workOrdersSLAData.loading,
        configObj: {
          customButtons: (
            <NuvoTooltip
              content={
                <>
                  <div>
                    Threshold Time to Notice -{' '}
                    {
                      queries.workOrdersSLAData.data?.workOrdersSLAData?.find(
                        (a: any) => a?.key === 'Time To Notice',
                      )?.thresholdValue
                    }{' '}
                    days
                  </div>
                  <div>
                    Threshold Time to Acknowledge -{' '}
                    {
                      queries.workOrdersSLAData.data?.workOrdersSLAData?.find(
                        (a: any) => a?.key === 'Time To Acknowledge',
                      )?.thresholdValue
                    }{' '}
                    days
                  </div>
                  <div>
                    Threshold Time to Restore -{' '}
                    {
                      queries.workOrdersSLAData.data?.workOrdersSLAData?.find(
                        (a: any) => a?.key === 'Time To Resolve',
                      )?.thresholdValue
                    }{' '}
                    days
                  </div>
                </>
              }>
              <FAIcon icon="circle-info" size="xs" />
            </NuvoTooltip>
          ),
        },
        element: (
          <StackedBarChart
            colors={{
              green: '#52C352',
              atThreshold: '#FDCE01',
              red: '#FB533E',
            }}
            dataSource={
              queries.workOrdersSLAData.data?.workOrdersSLAData as {
                key: string;
                statsPoints: DashboardStatsPoint[];
              }[]
            }
            noDataText="No work orders."
            yTickInterval={1}
            setup={{
              valueAxis: 'Number of Work Orders',
              argumentAxis: '',
            }}
          />
        ),
      },
    };
  }, [navigate, ...Object.values(queries), session.user?.id]);

  const { value: configVisible, on: showConfig, off: hideConfig } = useSwitch(false);

  const actions = useMemo<PageAction[]>(
    () => [
      {
        id: 'customize',
        type: 'button',
        alwaysShow: true,
        props: {
          icon: 'sliders',
          label: 'Customize',
          responsive: true,
          onClick: showConfig,
        },
      },
    ],
    [showConfig],
  );
  return (
    <SubPage title="Dashboard" isLoading={loading} type="dashboard">
      <PageActions actions={actions} />
      <DashboardConfig
        visible={configVisible}
        onHide={hideConfig}
        items={items}
        onSave={saveItems}
        defaultItems={defaultItems}
        defaultItemsJson={defaultItemsJson}
      />
      <Dashboard>
        {items
          .filter((item) => item.show)
          .map((item) => {
            const {
              element,
              loading,
              data: itemData,
              configObj,
            } = itemRenderers[item.key as ItemKeys];

            return (
              <DashboardItem
                key={item.key}
                title={item.title}
                subtitle={item.subtitle}
                data={itemData as DashboardStatsPoint[]}
                loading={loading}
                customButtons={configObj?.customButtons}
                {...item.colSpan}>
                {element}
              </DashboardItem>
            );
          })}
      </Dashboard>
    </SubPage>
  );
};

export default React.memo(DashboardPage);
